/**
 * Case status — label + tone (breadcrumb redesign B-6 / B-7,
 * `ai-docs/lexide-breadcrumb-case-summary-redesign-plan.md` §3.3).
 *
 * Replaces two defects that shipped together:
 *
 * 1. **Status rendered as a raw enum** in eleven lawyer-facing places —
 *    `admin_appeal` where the lawyer expects "Recurso administrativo".
 * 2. **Eight duplicated colour maps**, the largest covering six of the 41
 *    values. Two of them asserted something FALSE: `CasesListLayout.tsx:86`
 *    painted every *unknown* status with the green "active" swatch, so an
 *    abandoned case read as running.
 *
 * ## Why families and not per-value colour (B-7)
 *
 * Per-value colour for 41 values is unmaintainable and produces a rainbow.
 * Five semantic families give every value a *meant* colour instead of 33
 * falling through to a default — and a family stays correct when a matter type
 * adds a status, which a per-value map does not.
 *
 * ## The open-vocabulary rule
 *
 * The status vocabulary is OPEN: nine matter types define their own lifecycles
 * in lx-kernel and a tenth can be added without touching the SPA. So:
 *
 * - **the label** falls back to a humanizer (`admin_appeal → "Admin appeal"`),
 *   never to a raw key and never to a blank;
 * - **the tone** falls back to `unknown`, which renders NEUTRAL — deliberately
 *   not `active`. Claiming a case is running when we do not recognise its
 *   status is the exact lie `CasesListLayout` was telling.
 *
 * `case-status.test.ts` reads the nine vocabularies out of lx-kernel's source
 * and fails if any value here is unassigned, so adding a status there breaks a
 * test here rather than silently greying a chip.
 */

import { useTranslation } from "react-i18next";

/** Semantic families (B-7). `unknown` is the honest fallback, not a colour. */
export type StatusTone = "intake" | "active" | "resolved" | "adverse" | "paused" | "unknown";

/**
 * Every value across the nine lx-kernel vocabularies, assigned to a family.
 *
 * Grouped by the vocabulary that introduced each value so this stays auditable
 * against lx-kernel; several appear in more than one matter type.
 */
export const CASE_STATUS_TONES: Readonly<Record<string, StatusTone>> = {
  // ── not started ──────────────────────────────────────────────
  intake: "intake",
  draft: "intake",
  drafting: "intake",
  scoping: "intake",
  structuring: "intake",

  // ── in flight ────────────────────────────────────────────────
  active: "active",
  discovery: "active",
  trial: "active",
  hearing: "active",
  enforcement: "active",
  admin_defense: "active",
  admin_appeal: "active",
  judicial: "active",
  negotiating: "active",
  due_diligence: "active",
  title_review: "active",
  signing: "active",
  closing: "active",
  collecting: "active",
  analyzing: "active",
  remediating: "active",
  research: "active",
  // advisory's `review` is a work phase (drafting → research → review →
  // delivered). Contract's `under_review` is the opposite — waiting on someone
  // else — and is `paused`. Two similar words, two different meanings.
  review: "active",

  // ── done, well ───────────────────────────────────────────────
  closed: "resolved",
  signed: "resolved",
  delivered: "resolved",
  paid: "resolved",
  settlement: "resolved",
  reported: "resolved",
  post_closing: "resolved",
  registration: "resolved",

  // ── done, badly ──────────────────────────────────────────────
  terminated: "adverse",
  withdrawn: "adverse",
  abandoned: "adverse",
  cancelled: "adverse",
  escalated: "adverse",
  orphaned: "adverse",
  superseded: "adverse",

  // ── waiting on someone ───────────────────────────────────────
  suspended: "paused",
  pending_approval: "paused",
  under_review: "paused",
} as const;

/**
 * The semantic family for a status. Unrecognised → `unknown` (neutral), never
 * `active`.
 */
export function caseStatusTone(status: string | undefined | null): StatusTone {
  const key = status?.trim();
  if (!key) return "unknown";
  return CASE_STATUS_TONES[key] ?? "unknown";
}

/**
 * Tailwind classes per family. One place, so a status chip cannot drift between
 * the eleven surfaces that render one.
 *
 * `unknown` is deliberately the same muted slate as a disabled control: it
 * should read as "we do not know", not as a state.
 */
export const STATUS_TONE_CLASSES: Readonly<Record<StatusTone, string>> = {
  intake: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  active: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300",
  resolved: "bg-sky-100 text-sky-800 dark:bg-sky-950/50 dark:text-sky-300",
  adverse: "bg-rose-100 text-rose-800 dark:bg-rose-950/50 dark:text-rose-300",
  paused: "bg-amber-100 text-amber-900 dark:bg-amber-950/50 dark:text-amber-300",
  unknown: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
} as const;

/**
 * Humanize an unrecognised status: `admin_appeal → "Admin appeal"`.
 *
 * Exported for the label hook's `defaultValue` and for tests. Not a translation
 * — it is the honest last resort for a vocabulary this SPA does not yet know.
 */
export function humanizeStatus(status: string): string {
  const spaced = status.replace(/[_-]+/g, " ").trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : "";
}

/**
 * The lawyer-facing label for a status, in the active locale.
 *
 * Missing key → the humanizer, so a status added to lx-kernel tomorrow renders
 * readably today instead of showing `caseStatus.foo` or an empty chip.
 */
export function useCaseStatusLabel(status: string | undefined | null): string {
  const { t } = useTranslation();
  return caseStatusLabel(t, status);
}

/**
 * Non-hook form, for use inside a `.map()` where a hook cannot be called per
 * row. Take `t` once from `useTranslation()` at the component's top level and
 * pass it in.
 *
 * Same rule as the hook, deliberately sharing one implementation: a second copy
 * of "which key, which fallback" is exactly how the eight colour maps drifted.
 */
export function caseStatusLabel(
  t: (key: string, opts?: { defaultValue?: string }) => string,
  status: string | undefined | null,
): string {
  const key = status?.trim();
  if (!key) return "";
  return t(`caseStatus.${key}`, { defaultValue: humanizeStatus(key) });
}
